import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { Container, FormFooter, Navigation, NavBar, Select } from '../../components';
import Days from '../../components/schedule_cal/day_select';
import { COLOR, FONT } from '../../../constants/styles';
import {
  resetAppointmentWindowsStatus,
  saveSchedulerAppointmentWindow as saveSchedulerAppointmentWindowAction,
} from './slice';
import { REQUESTING, SUCCESS } from '../../../actions/statusTypes';
import { getSelectedDays, getSelectedIsoWeekdays } from './scheduler_appointment_window_utils';

const SAVE_SUCCESS_MESSAGE = 'Scheduler appointment window saved.';
const SCHEDULING_WINDOW_OPTIONS = Array.from({ length: 13 }, (_, index) => {
  const id = 30 + index * 5;
  return { id, label: String(id) };
});
const CUTOFF_WINDOW_OPTIONS = Array.from({ length: 31 }, (_, index) => ({
  id: index,
  label: String(index),
}));

function normalizeSelectedNumber(value) {
  if (value == null || value === '') return null;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function withCurrentValueOption(options, value) {
  const normalizedValue = normalizeSelectedNumber(value);

  if (normalizedValue == null || options.some((option) => option.id === normalizedValue)) {
    return options;
  }

  return [{ id: normalizedValue, label: String(normalizedValue) }].concat(options);
}

function toFormState(item) {
  return {
    schedulingWindow: normalizeSelectedNumber(item?.schedulingWindow),
    cutoffBusinessDays: normalizeSelectedNumber(item?.cutoffBusinessDays ?? 0),
    selectedDays: getSelectedDays(item?.nonBusinessDays, {
      defaultSelectedOnNull: item?.nonBusinessDays === null,
    }),
  };
}

const SchedulerAppointmentWindowForm = memo((props) => {
  useEffect(NavBar.setup(props), []);

  const dispatch = useDispatch();
  const employerId = props.employer?.employerId;
  const saveAppointmentWindowStatus = useSelector(
    (state) => state.screens.airforceSetup.saveAppointmentWindowStatus
  );
  const saveAppointmentWindowError = useSelector(
    (state) => state.screens.airforceSetup.saveAppointmentWindowError
  );

  const [focusedSelect, setFocusedSelect] = useState(null);
  const [formState, setFormState] = useState(() => toFormState(props.item));

  useEffect(() => {
    dispatch(resetAppointmentWindowsStatus());
    return () => {
      dispatch(resetAppointmentWindowsStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    setFormState(toFormState(props.item));
  }, [props.item]);

  const isSaving = saveAppointmentWindowStatus === REQUESTING;
  const schedulingWindowOptions = useMemo(() => {
    return withCurrentValueOption(SCHEDULING_WINDOW_OPTIONS, formState.schedulingWindow);
  }, [formState.schedulingWindow]);
  const cutoffWindowOptions = useMemo(() => {
    return withCurrentValueOption(CUTOFF_WINDOW_OPTIONS, formState.cutoffBusinessDays);
  }, [formState.cutoffBusinessDays]);
  const selectedIsoWeekdays = useMemo(() => {
    return getSelectedIsoWeekdays(formState.selectedDays);
  }, [formState.selectedDays]);

  const validationMessage = useMemo(() => {
    if (!props.item?.appointmentType) {
      return 'Appointment type is required.';
    }

    if (formState.schedulingWindow == null) {
      return 'Scheduling Window is required.';
    }

    if (formState.cutoffBusinessDays == null) {
      return 'Cutoff Window is required.';
    }

    return null;
  }, [formState.cutoffBusinessDays, formState.schedulingWindow, props.item]);

  const handleChangeField = useCallback((field, value) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const handleDayPress = useCallback((isoWeekDay) => {
    setFormState((current) => ({
      ...current,
      selectedDays: {
        ...current.selectedDays,
        [isoWeekDay]: !current.selectedDays?.[isoWeekDay],
      },
    }));
  }, []);

  const handleSelectFocus = useCallback((field) => {
    setFocusedSelect(field);
  }, []);

  const handleSelectBlur = useCallback(() => {
    setFocusedSelect(null);
  }, []);

  const handleCancel = useCallback(() => {
    Navigation.close(props);
  }, [props]);

  const handleSave = useCallback(() => {
    if (!props.item?.appointmentType || !employerId) return;

    dispatch(
      saveSchedulerAppointmentWindowAction({
        employerId,
        appointmentWindow: {
          employerAppointmentWindowsId: props.item.employerAppointmentWindowsId,
          employerId,
          appointmentType: props.item.appointmentType,
          schedulingWindow: Number(formState.schedulingWindow),
          cutoffBusinessDays: Number(formState.cutoffBusinessDays),
          nonBusinessDays: selectedIsoWeekdays,
        },
      })
    );
  }, [dispatch, employerId, formState, props.item, selectedIsoWeekdays]);

  const footer = (
    <FormFooter
      onCancel={handleCancel}
      onSave={handleSave}
      cancelLabel={saveAppointmentWindowStatus === SUCCESS ? 'Done' : 'Cancel'}
      saveLabel={isSaving ? 'Saving…' : 'Save'}
      saveDisabled={isSaving || !!validationMessage || !props.item?.appointmentType || !employerId}
    />
  );

  return (
    <Container
      scrollView={true}
      stickyFooter={footer}
      contentContainerStyle={styles.contentContainer}
      renderActivityOverlay={isSaving}
      successMessage={saveAppointmentWindowStatus === SUCCESS ? SAVE_SUCCESS_MESSAGE : null}
      errorMessage={saveAppointmentWindowError?.message || null}
    >
      <View
        style={[
          styles.formRow,
          focusedSelect === 'schedulingWindow' || focusedSelect === 'cutoffBusinessDays'
            ? styles.focusedFormRow
            : null,
        ]}
      >
        <View
          style={[
            styles.fieldContainer,
            focusedSelect === 'schedulingWindow' ? styles.focusedSelectField : null,
          ]}
        >
          <Select
            required={true}
            closeOnSelect={true}
            webMode={true}
            label="Scheduling Window (calendar days)"
            options={schedulingWindowOptions}
            value={formState.schedulingWindow}
            onSelect={(value) => handleChangeField('schedulingWindow', value)}
            onFocus={() => handleSelectFocus('schedulingWindow')}
            onBlur={handleSelectBlur}
            containerStyle={styles.selectFieldContainer}
            nativeContainerStyle={styles.selectFieldContainer}
            menuPlacement="bottom"
          />
        </View>
        <View
          style={[
            styles.fieldContainer,
            focusedSelect === 'cutoffBusinessDays' ? styles.focusedSelectField : null,
          ]}
        >
          <Select
            required={true}
            closeOnSelect={true}
            webMode={true}
            label="Cutoff Window (Business days)"
            options={cutoffWindowOptions}
            value={formState.cutoffBusinessDays}
            onSelect={(value) => handleChangeField('cutoffBusinessDays', value)}
            onFocus={() => handleSelectFocus('cutoffBusinessDays')}
            onBlur={handleSelectBlur}
            containerStyle={styles.selectFieldContainer}
            nativeContainerStyle={styles.selectFieldContainer}
            menuPlacement="bottom"
          />
        </View>
      </View>

      <View style={styles.formRow}>
        <View style={styles.fieldContainer}>
          <Text style={styles.helperText}>
            Non Business Days don't count for the cutoff window. Select the weekdays that should be
            treated as non-business days.
          </Text>
          <Text style={styles.fieldLabel}>Non Business Days</Text>
          <Days
            headerLabel={false}
            selectedDays={formState.selectedDays}
            onPressDay={handleDayPress}
            containerStyle={styles.daysContainer}
          />
        </View>
      </View>

      {!!validationMessage && <Text style={styles.validationText}>{validationMessage}</Text>}
    </Container>
  );
});

const styles = StyleSheet.create({
  contentContainer: {
    padding: 20,
  },
  formRow: {
    flexDirection: 'row',
    gap: 20,
  },
  focusedFormRow: {
    zIndex: 1020,
  },
  fieldContainer: {
    flex: 1,
  },
  focusedSelectField: {
    zIndex: 1000,
  },
  selectFieldContainer: {
    flex: 1,
  },
  helperText: {
    ...FONT.regular,
    color: COLOR.text,
    marginBottom: 10,
    marginTop: 10,
  },
  fieldLabel: {
    ...FONT.semibold,
    color: COLOR.text,
    marginBottom: 6,
  },
  daysContainer: {
    borderTopWidth: 1,
    borderColor: 'rgb(241, 241, 241)',
    marginBottom: 8,
  },
  warningText: {
    ...FONT.regular,
    color: COLOR.textLight,
    marginBottom: 10,
  },
  validationText: {
    ...FONT.regular,
    color: COLOR.error,
    marginTop: 10,
    alignSelf: 'flex-end',
  },
});

export default SchedulerAppointmentWindowForm;
